import React from 'react';
import { useTranslation } from 'react-i18next';

interface FilterPreset {
  id: string;
  label: string;
  values: {
    brightness: number;
    contrast: number;
    saturation: number;
    blur: number;
    grayscale: number;
    sepia: number;
  };
}

interface ImageFiltersProps {
  activeFilter: string;
  onFilterChange: (filter: FilterPreset) => void;
  onCustomChange: (key: string, value: number) => void;
  customValues: {
    brightness: number;
    contrast: number;
    saturation: number;
    blur: number;
    grayscale: number;
    sepia: number;
  };
}

const FILTER_PRESETS: FilterPreset[] = [
  { id: 'normal', label: 'Normal', values: { brightness: 100, contrast: 100, saturation: 100, blur: 0, grayscale: 0, sepia: 0 } },
  { id: 'warm', label: 'Warm', values: { brightness: 105, contrast: 110, saturation: 120, blur: 0, grayscale: 0, sepia: 20 } },
  { id: 'cool', label: 'Cool', values: { brightness: 100, contrast: 105, saturation: 90, blur: 0, grayscale: 0, sepia: 0 } },
  { id: 'vintage', label: 'Vintage', values: { brightness: 110, contrast: 90, saturation: 70, blur: 0, grayscale: 0, sepia: 40 } },
  { id: 'bw', label: 'B&W', values: { brightness: 100, contrast: 120, saturation: 0, blur: 0, grayscale: 100, sepia: 0 } },
  { id: 'dramatic', label: 'Dramatic', values: { brightness: 90, contrast: 140, saturation: 110, blur: 0, grayscale: 0, sepia: 0 } },
];

const ImageFiltersPanel: React.FC<ImageFiltersProps> = ({
  activeFilter,
  onFilterChange,
  onCustomChange,
  customValues
}) => {
  const { t } = useTranslation();

  const sliders = [
    { key: 'brightness', label: t('imageStudio.filters.brightness', 'Brightness'), min: 0, max: 200, step: 1 },
    { key: 'contrast', label: t('imageStudio.filters.contrast', 'Contrast'), min: 0, max: 200, step: 1 },
    { key: 'saturation', label: t('imageStudio.filters.saturation', 'Saturation'), min: 0, max: 200, step: 1 },
    { key: 'blur', label: t('imageStudio.filters.blur', 'Blur'), min: 0, max: 20, step: 0.5 },
    { key: 'grayscale', label: t('imageStudio.filters.grayscale', 'Grayscale'), min: 0, max: 100, step: 1 },
    { key: 'sepia', label: t('imageStudio.filters.sepia', 'Sepia'), min: 0, max: 100, step: 1 },
  ];

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('imageStudio.filters.presets', 'Presets')}
      </h3>

      {/* Presets Grid */}
      <div className="grid grid-cols-3 gap-2">
        {FILTER_PRESETS.map((preset) => (
          <button
            key={preset.id}
            onClick={() => onFilterChange(preset)}
            className={`p-2 rounded-lg text-xs font-medium text-center transition-colors ${
              activeFilter === preset.id
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 ring-2 ring-primary-500'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {t(`imageStudio.filters.${preset.id}`, preset.label)}
          </button>
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('imageStudio.filters.adjustments', 'Adjustments')}
        </h3>

        {/* Sliders */}
        <div className="space-y-3">
          {sliders.map((slider) => (
            <div key={slider.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-600 dark:text-gray-400">{slider.label}</label>
                <span className="text-xs text-gray-500 w-10 text-right">
                  {customValues[slider.key as keyof typeof customValues]}
                </span>
              </div>
              <input
                type="range"
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={customValues[slider.key as keyof typeof customValues]}
                onChange={(e) => onCustomChange(slider.key, parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full appearance-none cursor-pointer accent-primary-600"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { FILTER_PRESETS };
export type { FilterPreset };
export default ImageFiltersPanel;
